/**
 * TTS feature: speak current slide's <aside class="notes"> via Web Speech API.
 * Orchestrates tts-queue (parsing), tts-actions (DOM actions), tts-speech (voice + speak).
 * When RevealNotesTTS is present, orchestrator delegates to it; otherwise this runs.
 */

import type { LecturaHostAdapter } from '../core/types';
import {
  getStoredAutoAdvance,
  getStoredVoice,
  getStoredRate,
  getStoredVolume,
  getStoredLang,
  getStoredHighlightRgb,
  getNotesSourceLang
} from '../services/tts-settings';
import { translateNotes } from '../services/tts-translate';
import { parseNotes, stripMarkdownForTTS, formatForSubtitle, type QueueItem } from '../services/tts-queue';
import { runAction } from '../services/tts-actions';
import { stopTtsSound } from '../services/tts-sound';
import { pickBestVoice, speakOne } from '../services/tts-speech';

function applyHighlightColor(rgb: string): void {
  if (!rgb || typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--notes-tts-highlight-rgb', rgb);
  const reveal = document.querySelector('.reveal');
  if (reveal) (reveal as HTMLElement).style.setProperty('--notes-tts-highlight-rgb', rgb);
}

export interface TTSSlots {
  ttsSlot?: HTMLElement;
}

export interface TTSApi {
  toggleTTS: () => void;
  /** Run a single TTS codeword from outside (e.g. MQTT). Uses current slide as root. Resolves when the action completes. */
  runCodeword: (item: unknown) => Promise<void>;
}

export function registerTTS(adapter: LecturaHostAdapter, slots: TTSSlots = {}): TTSApi | undefined {
  const synthRaw = typeof window !== 'undefined' && (window.speechSynthesis || (window as unknown as { webkitSpeechSynthesis?: SpeechSynthesis }).webkitSpeechSynthesis);
  if (!synthRaw) return undefined;
  const synth = synthRaw as SpeechSynthesis;

  const ttsSlot = slots.ttsSlot ?? document.querySelector<HTMLElement>('.lectura-bar-tts');
  if (!ttsSlot) return undefined;

  let enabled = false;
  let speaking = false;
  let runId = 0;
  let subtitleEl: HTMLElement | null = null;
  let progressBarEl: HTMLElement | null = null;
  let progressFillEl: HTMLElement | null = null;
  const activePersistentHighlights: Element[] = [];
  const zoomRuntime = {
    level: 1,
    autoResetTimer: null as ReturnType<typeof setTimeout> | null,
    previousBodyStyles: null as {
      transform: string;
      transformOrigin: string;
      transition: string;
      zoom: string;
      position: string;
      left: string;
      top: string;
      width: string;
      height: string;
    } | null
  };
  const queueControls = { nextPhrase: (): void => {}, prevPhrase: (): void => {} };
  /** Persistent highlights for externally triggered codewords (e.g. MQTT), separate from main TTS queue. */
  const externalPersistentHighlights: Element[] = [];

  function getOverlayContainer(): HTMLElement | null {
    const wrapper = document.querySelector<HTMLElement>('.rf-slides-chat-wrap .reveal, .reveal');
    if (!wrapper) return null;
    const pos = window.getComputedStyle(wrapper).position;
    if (pos === 'static') wrapper.style.position = 'relative';
    return wrapper;
  }

  function ensureSubtitleEl(): HTMLElement | null {
    if (subtitleEl) return subtitleEl;
    const wrapper = getOverlayContainer();
    if (!wrapper) return null;
    subtitleEl = document.createElement('div');
    subtitleEl.className = 'notes-tts-subtitle';
    subtitleEl.setAttribute('aria-live', 'polite');
    subtitleEl.setAttribute('aria-atomic', 'true');
    subtitleEl.style.display = 'none';
    wrapper.appendChild(subtitleEl);
    return subtitleEl;
  }

  function ensureProgressEl(): { bar: HTMLElement; fill: HTMLElement } | null {
    if (progressBarEl && progressFillEl) return { bar: progressBarEl, fill: progressFillEl };
    const wrapper = getOverlayContainer();
    if (!wrapper) return null;
    progressBarEl = document.createElement('div');
    progressBarEl.className = 'notes-tts-progress';
    progressBarEl.setAttribute('aria-hidden', 'true');
    progressBarEl.style.display = 'none';
    progressFillEl = document.createElement('div');
    progressFillEl.className = 'notes-tts-progress-fill';
    progressBarEl.appendChild(progressFillEl);
    wrapper.appendChild(progressBarEl);
    return { bar: progressBarEl, fill: progressFillEl };
  }

  function showSubtitle(text: string): void {
    if (!text) {
      hideSubtitle();
      return;
    }
    const el = ensureSubtitleEl();
    if (!el) return;
    el.innerHTML = formatForSubtitle(text);
    el.style.display = 'block';
  }

  function hideSubtitle(): void {
    if (subtitleEl) {
      subtitleEl.innerHTML = '';
      subtitleEl.style.display = 'none';
    }
  }

  function updateProgress(completed: number, total: number): void {
    const p = ensureProgressEl();
    if (!p || total <= 0) return;
    p.bar.style.display = 'block';
    const pct = Math.min(100, Math.round((completed / total) * 100));
    p.fill.style.width = pct + '%';
  }

  function clearProgress(): void {
    if (progressBarEl) progressBarEl.style.display = 'none';
  }

  function getNotesForSlide(slideEl: HTMLElement | null): string | null {
    if (!slideEl?.querySelector) return null;
    if (slideEl.hasAttribute?.('data-tts-skip')) return null;
    const aside = slideEl.querySelector('aside.notes');
    return aside ? aside.textContent?.trim() ?? null : null;
  }

  function getFirstNameOnly(s: string): string {
    const t = String(s || '').trim().replace(/\s+/g, ' ');
    return t ? t.split(' ')[0]!.trim() : '';
  }

  function getNameForTTS(fallback?: string): string {
    const w = typeof window !== 'undefined' ? window : null;
    if (w && typeof (w as unknown as { getStudentNameForTTS?: () => string }).getStudentNameForTTS === 'function') {
      const name = (w as unknown as { getStudentNameForTTS: () => string }).getStudentNameForTTS();
      const first = getFirstNameOnly(name);
      if (first) return first;
    }
    const user = adapter.getCurrentUser();
    if (user && !user.isAnonymous) {
      const first = getFirstNameOnly(user.displayName || '');
      if (first) return first;
      const email = (user.email || '').trim();
      if (email) return email.split('@')[0] || 'everyone';
    }
    const fb = getFirstNameOnly(fallback || '');
    return fb || 'everyone';
  }

  const NAME_PLACEHOLDER_REGEX = /\u200B__TTS_NAME__(?::([^\u200B]*))?\u200B/g;
  function resolveNameInText(text: string): string {
    if (!text || text.indexOf('\u200B__TTS_NAME__') === -1) return text;
    return text.replace(NAME_PLACEHOLDER_REGEX, (_, fallback: string) => getNameForTTS(fallback));
  }

  function processQueue(
    queue: QueueItem[],
    slideEl: HTMLElement | null,
    onDone: () => void,
    onSubtitle: (text: string) => void,
    onProgress: (completed: number, total: number) => void,
    isCancelled: () => boolean,
    controls: { nextPhrase: () => void; prevPhrase: () => void }
  ): void {
    let idx = 0;
    const total = queue.length;
    let currentRunId = 0;

    function doSpeak(textToSpeak: string, textForSubtitle: string): void {
      if (isCancelled()) return;
      const myRunId = currentRunId;
      const speakText = stripMarkdownForTTS(textToSpeak);
      const voices = synth.getVoices();
      const preferredLang = getStoredLang();
      const voice = pickBestVoice(voices, preferredLang, getStoredVoice());
      speakOne(
        synth,
        speakText,
        { rate: getStoredRate(), volume: getStoredVolume(), voice },
        {
          onStart: () => {
            if (myRunId === currentRunId) onSubtitle(textForSubtitle);
          },
          onEnd: () => {
            if (myRunId !== currentRunId || isCancelled()) return;
            onSubtitle('');
            next();
          },
          onError: () => {
            if (myRunId === currentRunId) onSubtitle('');
            next();
          }
        }
      );
    }

    function next(): void {
      if (isCancelled()) return;
      if (idx >= total) {
        onSubtitle('');
        onProgress(total, total);
        controls.nextPhrase = () => {};
        controls.prevPhrase = () => {};
        onDone();
        return;
      }
      onProgress(idx, total);
      const item = queue[idx++]!;
      if (item.type === 'speak' && item.text) {
        const textWithName = resolveNameInText(item.text);
        doSpeak(textWithName, textWithName);
      } else if (item.type === 'pause') {
        const pauseRunId = currentRunId;
        setTimeout(() => {
          if (pauseRunId !== currentRunId || isCancelled()) return;
          next();
        }, item.ms);
      } else {
        const actionRunId = currentRunId;
        runAction(item, slideEl, () => {
          if (actionRunId !== currentRunId || isCancelled()) return;
          next();
        }, { activePersistentHighlights, zoomRuntime });
      }
    }

    controls.nextPhrase = (): void => {
      currentRunId++;
      synth.cancel();
      stopTtsSound();
      if (idx < total) idx++;
      next();
    };
    controls.prevPhrase = (): void => {
      currentRunId++;
      synth.cancel();
      stopTtsSound();
      let targetIdx = idx - 1;
      while (targetIdx > 0 && queue[targetIdx - 1]?.type !== 'speak') targetIdx--;
      if (targetIdx > 0) targetIdx--;
      idx = Math.max(0, targetIdx);
      next();
    };
    next();
  }

  function runQueueForSlide(
    queue: QueueItem[],
    slideEl: HTMLElement | null,
    existingRunId?: number
  ): void {
    if (queue.length === 0) return;
    const voices = synth.getVoices();
    if (voices.length === 0) {
      setTimeout(() => speakSlide(slideEl), 400);
      return;
    }
    const myRunId = existingRunId ?? ++runId;
    queueControls.nextPhrase = () => {};
    queueControls.prevPhrase = () => {};
    processQueue(
      queue,
      slideEl,
      () => {
        queueControls.nextPhrase = () => {};
        queueControls.prevPhrase = () => {};
        if (enabled && getStoredAutoAdvance() && typeof Reveal !== 'undefined' && typeof (Reveal as { next?: () => void }).next === 'function') {
          const current = (Reveal as { getCurrentSlide?: () => HTMLElement | null }).getCurrentSlide?.();
          const isVideoSlide = current && (current.hasAttribute('data-youtube-id') || current.classList.contains('slide-youtube-video'));
          if (!isVideoSlide) (Reveal as { next: () => void }).next();
        }
      },
      showSubtitle,
      updateProgress,
      () => !enabled || myRunId !== runId,
      queueControls
    );
  }

  function speakSlide(slideEl: HTMLElement | null, overrideTargetLang?: string): void {
    if (!enabled || !slideEl) return;
    synth.cancel();
    stopTtsSound();
    hideSubtitle();
    clearProgress();
    for (const el of activePersistentHighlights) el.classList.remove('notes-tts-highlight-on');
    activePersistentHighlights.length = 0;
    const raw = getNotesForSlide(slideEl);
    if (!raw) return;

    const sourceLang = getNotesSourceLang();
    const targetLang = overrideTargetLang != null && overrideTargetLang.trim() !== ''
      ? overrideTargetLang.trim().toLowerCase().substring(0, 2)
      : getStoredLang();

    if (sourceLang === targetLang) {
      const queue = parseNotes(raw);
      runQueueForSlide(queue, slideEl);
      return;
    }

    const myRunId = ++runId;
    translateNotes(raw, sourceLang, targetLang)
      .then((translated) => {
        if (!enabled || runId !== myRunId) return;
        const queue = parseNotes(translated);
        runQueueForSlide(queue, slideEl, myRunId);
      })
      .catch(() => {
        if (!enabled || runId !== myRunId) return;
        const queue = parseNotes(raw);
        runQueueForSlide(queue, slideEl, myRunId);
      });
  }

  function onSlideChange(): void {
    speaking = false;
    runId++;
    queueControls.nextPhrase = () => {};
    queueControls.prevPhrase = () => {};
    synth.cancel();
    stopTtsSound();
    hideSubtitle();
    clearProgress();
    activePersistentHighlights.length = 0;
    for (const el of activePersistentHighlights) el.classList.remove('notes-tts-highlight-on');
    updateButton();
    const slideEl = adapter.getCurrentSlide?.() ?? null;
    if (slideEl) speakSlide(slideEl);
  }

  function stopTTSFully(): void {
    enabled = false;
    speaking = false;
    runId++;
    queueControls.nextPhrase = () => {};
    queueControls.prevPhrase = () => {};
    synth.cancel();
    stopTtsSound();
    hideSubtitle();
    clearProgress();
    for (const el of activePersistentHighlights) el.classList.remove('notes-tts-highlight-on');
    activePersistentHighlights.length = 0;
    updateButton();
  }

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'notes-tts-btn lectura-bar-btn';
  btn.setAttribute('aria-label', 'Toggle speak slide notes');
  // TTS off: speaker with X (mute). TTS on: speaker with sound waves.
  btn.innerHTML =
    '<span class="notes-tts-icon notes-tts-icon-off" aria-hidden="true" data-tts-state="off"><svg class="lectura-bar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="14" y1="8" x2="22" y2="16"/><line x1="14" y1="16" x2="22" y2="8"/></svg></span>' +
    '<span class="notes-tts-icon notes-tts-icon-on" aria-hidden="true" hidden data-tts-state="on"><svg class="lectura-bar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg></span>';
  ttsSlot.appendChild(btn);

  function updateButton(): void {
    btn.classList.toggle('active', enabled);
    btn.classList.toggle('speaking', enabled && speaking);
    btn.setAttribute('aria-pressed', String(enabled));
    const offEl = btn.querySelector('.notes-tts-icon-off');
    const onEl = btn.querySelector('.notes-tts-icon-on');
    if (offEl) (offEl as HTMLElement).hidden = enabled;
    if (onEl) (onEl as HTMLElement).hidden = !enabled;
    btn.title = enabled
      ? speaking
        ? 'Speaking notes: on (T)'
        : 'Speaking notes: on (T)'
      : 'Speak slide notes (T)';
  }

  function toggleTTS(): void {
    if (enabled) {
      stopTTSFully();
      return;
    }
    enabled = true;
    speakSlide(adapter.getCurrentSlide?.() ?? null);
    updateButton();
  }

  btn.addEventListener('click', toggleTTS);

  adapter.subscribeSlideChange(() => {
    if (enabled) onSlideChange();
  });

  const highlightRgb = getStoredHighlightRgb() || adapter.getHighlightRgb?.();
  applyHighlightColor(highlightRgb);
  updateButton();

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      try {
        stopTTSFully();
      } catch {}
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        try {
          stopTTSFully();
        } catch {}
      }
    });
    window.addEventListener('lectura-tts-lang-changed', (ev: Event) => {
      if (!enabled) return;
      const newLang = (ev as CustomEvent<{ lang?: string }>).detail?.lang;
      stopTTSFully();
      enabled = true;
      updateButton();
      speakSlide(adapter.getCurrentSlide?.() ?? null, newLang);
    });
    window.addEventListener('lectura-tts-voice-changed', () => {
      if (!enabled) return;
      stopTTSFully();
      enabled = true;
      updateButton();
      speakSlide(adapter.getCurrentSlide?.() ?? null);
    });
  }

  function runCodeword(item: unknown): Promise<void> {
    return new Promise((resolve) => {
      if (!item || typeof item !== 'object' || !('type' in item) || typeof (item as { type: string }).type !== 'string') {
        resolve();
        return;
      }
      const slideEl = adapter.getCurrentSlide?.() ?? null;
      runAction(item as QueueItem, slideEl, () => resolve(), { activePersistentHighlights: externalPersistentHighlights, zoomRuntime });
    });
  }

  return { toggleTTS, runCodeword };
}
